import { useEffect, useRef } from 'react';
import { get } from 'lodash';
import { useQuery } from '@tanstack/react-query';
import metricsStore from '@globalStore/metricsStore';
import {
  getPreDefinedMetricsMeta,
  getUserDefinedMetricsMeta,
  getFlowConfigMeta,
  getFrustrationSignalConfigMeta,
} from '@api/meta';
import presetStore from '@src/globalStore/presetsStore';
import limitationStore from '@src/globalStore/limitationStore';

const REFETCH_DELAY = 10 * 1000;

function useMetricsMeta() {
  const lastUseDefinedFetchedRef = useRef<number>(0);
  const lastFrustrationFetchedRef = useRef<number>(0);
  const lastFlowFetchedRef = useRef<number>(0);
  const setPreDefinedMetricsMeta = metricsStore((state) => state.mergePreDefinedMetricsMeta);
  const setUserDefinedMetricsMeta = metricsStore((state) => state.mergeUserDefinedMetricsMeta);
  const setFlowConfigMeta = metricsStore((state) => state.setFlowConfigMeta);
  const setFlowMetricsMeta = metricsStore((state) => state.setFlowMetricsMeta);
  const setFrustrationSignalMetricsMeta = metricsStore(
    (state) => state.setFrustrationSignalMetricsMeta,
  );
  const setFrustrationSignalConfigMeta = metricsStore(
    (state) => state.setFrustrationSignalConfigMeta,
  );
  const setLimitations = limitationStore((state) => state.setLimitations);
  const setFlowPresets = presetStore((state) => state.setFlowPresets);
  const setPresets = presetStore((state) => state.setPresets);

  const { data: preDefinedMetrics, isFetching: isPreDefinedMetricsLoading } = useQuery(
    ['pre-defined-metrics-meta'],
    async () => {
      const response = await getPreDefinedMetricsMeta({});
      return get(response, ['data'], {});
    },
    {
      retry: 3,
      staleTime: Infinity,
      cacheTime: Infinity,
      refetchOnWindowFocus: false,
    },
  );

  const {
    data: userDefinedMetrics,
    isFetching: isUserDefinedMetricsLoading,
    refetch: reFetchUserDefinedMetricsMeta,
  } = useQuery(
    ['user-defined-metrics-meta'],
    async () => {
      const response = await getUserDefinedMetricsMeta({ forceUpdate: true });
      lastUseDefinedFetchedRef.current = Date.now();
      return get(response, ['data'], {});
    },
    {
      retry: 3,
      keepPreviousData: true,
      staleTime: 5 * 60 * 1000, // 5 min
    },
  );

  const {
    data: flowConfigMeta,
    isFetching: isFlowConfigMetaLoading,
    refetch: reFetchFlowConfigMeta,
  } = useQuery(
    ['flow-metrics-meta'],
    async () => {
      const resp = await getFlowConfigMeta({ forceUpdate: true });
      lastFlowFetchedRef.current = Date.now();
      return get(resp, ['data'], {});
    },
    {
      retry: 3,
      keepPreviousData: true,
      staleTime: 5 * 60 * 1000, // 5 min
    },
  );

  const {
    data: frustrationSignalMetricsMeta,
    isFetching: isFrustrationSignalMetricsLoading,
    refetch: reFetchFrustrationSignalMetricsMeta,
  } = useQuery(
    ['frustration-signal-metrics-meta'],
    async () => {
      const resp = await getFrustrationSignalConfigMeta({ forceUpdate: true });
      return get(resp, ['data'], {});
    },
    {
      retry: 3,
      keepPreviousData: true,
      staleTime: 5 * 60 * 1000, // 5 min
    },
  );

  useEffect(() => {
    if (!userDefinedMetrics) return;
    setUserDefinedMetricsMeta(userDefinedMetrics);
    setPresets();
  }, [userDefinedMetrics]);

  useEffect(() => {
    setPreDefinedMetricsMeta(preDefinedMetrics);
    setPresets();
  }, [preDefinedMetrics]);

  useEffect(() => {
    if (!flowConfigMeta) return;
    setFlowConfigMeta(flowConfigMeta);
    setFlowPresets(flowConfigMeta);
    setFlowMetricsMeta(flowConfigMeta);
    setLimitations();
  }, [flowConfigMeta]);

  useEffect(() => {
    if (!frustrationSignalMetricsMeta) return;
    setFrustrationSignalConfigMeta(frustrationSignalMetricsMeta);
    setFrustrationSignalMetricsMeta(frustrationSignalMetricsMeta);
    setLimitations();
  }, [frustrationSignalMetricsMeta]);

  const forceUpdateUserDefinedMetricsMeta = () => {
    const now = Date.now();
    if (now - lastUseDefinedFetchedRef.current < REFETCH_DELAY) {
      return userDefinedMetrics;
    }
    return reFetchUserDefinedMetricsMeta();
  };

  const forceUpdateFlowConfigMeta = () => {
    const now = Date.now();
    if (now - lastFlowFetchedRef.current < REFETCH_DELAY) {
      return flowConfigMeta;
    }
    return reFetchFlowConfigMeta();
  };

  const forceUpdateFrustrationSignalMetricsMeta = () => {
    const now = Date.now();
    if (now - lastFrustrationFetchedRef.current < REFETCH_DELAY) {
      return frustrationSignalMetricsMeta;
    }
    lastFrustrationFetchedRef.current = now;
    return reFetchFrustrationSignalMetricsMeta();
  };

  return {
    forceUpdateUserDefinedMetricsMeta,
    forceUpdateFlowConfigMeta,
    forceUpdateFrustrationSignalMetricsMeta,
    isFlowConfigMetaLoading,
    isFrustrationSignalMetricsLoading,
    isUserDefinedMetricsLoading,
    isPreDefinedMetricsLoading,
  };
}

export default useMetricsMeta;
